import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { runPhotoJob, jobResultPath } from '../lib/photoJobs.js';
import { runningInPlayApp } from '../lib/client.js';
import { ErrorNote, Empty, Pill, relativeDate } from '../components/ui.jsx';

/**
 * Req #1 + #3: the camera entry point.
 *
 * `capture="environment"` opens the rear camera directly on a phone, which is
 * the whole reason this is a PWA rather than a desktop page.
 */
export default function Capture() {
  const navigate = useNavigate();
  const health = useApi('/health');
  const usage = useApi('/usage');
  const receipts = useApi('/receipts?status=draft');
  const scans = useApi('/scans?limit=10');
  const locations = useApi('/locations');

  const [scanLocationId, setScanLocationId] = useState('');
  const receiptInput = useRef(null);
  const pantryInput = useRef(null);
  const { busy, error, setError, run } = useAction();
  const [progress, setProgress] = useState(null);
  // A finished check-up whose photo did not show everything. Held here rather
  // than navigated past, because the second shot is worth far more taken now,
  // standing at the open door, than remembered later from the review screen.
  const [partial, setPartial] = useState(null);

  const visionOff = health.data && !health.data.vision;
  // The cap gates the buttons, not just the banner above them: letting someone
  // frame the shot and sit through the upload before a 402 refuses it is a
  // worse version of the same news.
  const quotaSpent = usage.data?.remaining === 0;

  // Both paths go through the job pipeline. In the cloud the photo is PUT
  // straight to the bucket and the analysis runs in the follow-up request;
  // locally the bytes come through the API instead. Either way the call comes
  // back with the result rather than something to poll.
  const uploadReceipt = (file) => run(async () => {
    try {
      const job = await runPhotoJob({ jobType: 'receipt', files: [file], onProgress: setProgress });
      navigate(jobResultPath(job));
    } finally {
      setProgress(null);
    }
  });

  const uploadPantry = (file) => run(async () => {
    try {
      const job = await runPhotoJob({
        jobType: 'pantry_scan',
        files: [file],
        locationId: scanLocationId,
        onProgress: setProgress,
      });
      const path = jobResultPath(job);
      // The model was asked to say what it could not see. Saying it here, with
      // the camera one tap away, is the difference between a partial check-up
      // and a complete one.
      if (job.obscured_areas) setPartial({ path, note: job.obscured_areas });
      else navigate(path);
    } finally {
      setProgress(null);
    }
  });

  const pick = (ref) => () => { setError(null); setPartial(null); ref.current?.click(); };

  return (
    <div className="stack-lg">
      {/* What is left, before it matters. A cap discovered at the moment
          someone needs a scan is a worse experience than the cap itself. */}
      {usage.data && !visionOff && <QuotaBar usage={usage.data} />}

      {visionOff && (
        <div className="alert warn">
          <strong>Photo analysis is off.</strong>
          <div className="small" style={{ marginTop: 4 }}>
            Add <code>ANTHROPIC_API_KEY</code> to your <code>.env</code> and restart the server.
            You can still add inventory and recipes by hand.
          </div>
        </div>
      )}

      <ErrorNote error={error} />
      {busy && <div className="alert info"><span className="spinner" /> {progress || 'Working…'}</div>}

      <section className="stack">
        <div className="card stack">
          <div>
            <h2>Scan a receipt</h2>
            <p className="small muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Every line becomes a known item. Nothing is saved until you review it.
            </p>
          </div>
          <input
            ref={receiptInput}
            className="photo-input"
            tabIndex={-1}
            aria-hidden="true"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) uploadReceipt(file);
            }}
          />
          <button type="button" className="primary full" disabled={busy || visionOff || quotaSpent} onClick={pick(receiptInput)}>
            Photograph receipt
          </button>
        </div>

        {/* Not an inventory builder. A photo of a full pantry cannot build one;
            it can check one shelf against what the app expects to be there. */}
        <div className="card stack">
          <div>
            <h2>Check a shelf</h2>
            <p className="small muted" style={{ marginTop: 4, marginBottom: 0 }}>
              Photograph one shelf or drawer at a time; pull the front row forward so labels
              show. You get back what&rsquo;s still there, what&rsquo;s new, and what wasn&rsquo;t
              in the photo &mdash; to confirm, never an overwrite.
            </p>
          </div>
          {/* Required, not optional. Without a location there is nothing to
              check the photo against, and the save step refuses one anyway --
              so it is asked here rather than after the model has been paid. */}
          <label>
            <span className="label-text">Which shelf or drawer?</span>
            <select value={scanLocationId} onChange={(event) => setScanLocationId(event.target.value)}>
              <option value="">Choose one…</option>
              {(locations.data || []).map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </select>
          </label>
          <input
            ref={pantryInput}
            className="photo-input"
            tabIndex={-1}
            aria-hidden="true"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) uploadPantry(file);
            }}
          />
          <button
            type="button"
            className="primary full"
            disabled={busy || visionOff || quotaSpent || !scanLocationId}
            onClick={pick(pantryInput)}
          >
            Check this shelf
          </button>
          {!scanLocationId && (
            <p className="tiny muted" style={{ margin: 0 }}>
              Pick where you&rsquo;re standing first &mdash; a check-up is against one location.
            </p>
          )}
          {partial && (
            <div className="alert warn">
              <strong>Some of it wasn&rsquo;t visible.</strong>
              <div className="small" style={{ marginTop: 4 }}>{partial.note}</div>
              <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
                <button type="button" className="primary" disabled={busy} onClick={pick(pantryInput)}>
                  Take another photo of this location
                </button>
                <button type="button" disabled={busy} onClick={() => navigate(partial.path)}>
                  Review
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="card stack">
          <div>
            <h2>Scan a barcode</h2>
            <p className="small muted" style={{ marginTop: 4, marginBottom: 0 }}>
              One item at a time, read on this phone. No photo is uploaded and it works
              without the vision key &mdash; a barcode is a number to look up, not something
              to interpret.
            </p>
          </div>
          <Link to="/scan-barcode" className="button full" style={{ textAlign: 'center' }}>
            Scan a barcode
          </Link>
        </div>
      </section>

      {(receipts.data?.purchases?.length > 0) && (
        <section>
          <div className="section-title"><h2>Receipts to review</h2></div>
          <div className="card list">
            {receipts.data.purchases.map((purchase) => (
              <Link
                key={purchase.id}
                className="list-item"
                to={`/receipts/${purchase.id}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                <div className="grow">
                  <div className="truncate">{purchase.store_name || 'Unknown store'}</div>
                  <div className="tiny muted">
                    {relativeDate(purchase.purchased_at)} · {purchase.line_count} lines
                  </div>
                </div>
                {purchase.unmatched_count > 0 && <Pill tone="warn">{purchase.unmatched_count} new</Pill>}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="section-title"><h2>Recent check-ups</h2></div>
        {!scans.data?.scans?.length ? (
          <Empty title="No check-ups yet">
            Photograph one shelf to see what&rsquo;s still there, what&rsquo;s new, and what the
            app expected but didn&rsquo;t find.
          </Empty>
        ) : (
          <div className="card list">
            {scans.data.scans.map((scan) => (
              <Link
                key={scan.id}
                className="list-item"
                to={`/scans/${scan.id}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                <img className="thumb" src={scan.image_url} alt="" loading="lazy" />
                <div className="grow">
                  <div className="truncate">{scan.location_name || 'Unassigned'}</div>
                  <div className="tiny muted">
                    {relativeDate(scan.scanned_at)} · {scan.detected_count} found
                  </div>
                </div>
                <Pill tone={scan.status === 'applied' ? 'ok' : scan.status === 'failed' ? 'danger' : 'warn'}>
                  {scan.status}
                </Pill>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Photo scans left this month.
 *
 * Only AI calls are capped; everything else in the app is unlimited on both
 * plans, so this deliberately says "photo scans" rather than anything that
 * sounds like a general usage meter.
 */
/**
 * The one place a free plan is asked to become a paid one.
 *
 * Which offer appears is the server's decision rather than this component's
 * guess: a household that has already had its trial is offered the plan
 * directly, because dangling a second trial and refusing it at Checkout is
 * worse than never offering one.
 *
 * The trial takes a card and converts on its own. That is stated plainly and
 * BEFORE the button, because a trial that converts is subject to card-network
 * disclosure rules -- and because burying it is how a subscription becomes a
 * complaint.
 */
function UpgradeOffer({ eligible }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);

  const go = async (trial) => {
    setFailed(null);
    setBusy(true);
    try {
      const { url } = await api.post('/billing/checkout', { interval: 'monthly', trial });
      window.location.assign(url);
    } catch (error) {
      setFailed(error.message);
      setBusy(false);
    }
  };

  // The other place a purchase can start, and therefore the other place the
  // Play app must not offer one. Settings has the same conditional and the long
  // explanation of why (docs/play-listing.md section 0); the short version is
  // that Play's payments policy applies to what an app offers, and an app that
  // offers nothing owes nothing. A statement of where plans live is permitted;
  // a link to them is not, so this is text with nothing to tap.
  if (runningInPlayApp()) {
    return (
      <div className="small" style={{ marginTop: 8 }}>
        More scans come with a plan. Plans are managed at {window.location.host} in a web
        browser.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {eligible ? (
        <>
          <div className="small">
            <strong>Try 20 scans over 14 days.</strong> A card is required, and
            it becomes $5/month when the trial ends unless you cancel first. Cancel
            any time from Settings.
          </div>
          <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
            <button type="button" className="primary" disabled={busy} onClick={() => go(true)}>
              Start the 14-day trial
            </button>
            <button type="button" disabled={busy} onClick={() => go(false)}>
              Just subscribe
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="small">
            <strong>$5/month for 40 scans.</strong> Cancel any time from Settings.
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button type="button" className="primary" disabled={busy} onClick={() => go(false)}>
              Subscribe
            </button>
          </div>
        </>
      )}
      {failed && <div className="small" style={{ marginTop: 6 }}>{failed}</div>}
    </div>
  );
}

function QuotaBar({ usage }) {
  const {
    used, limit, remaining, plan, resets_at: resets,
    trialing, trial_days_left: trialDays, trial_eligible: eligible,
  } = usage;
  const spent = remaining === 0;
  const low = !spent && remaining <= 2;
  // The offer belongs here and only here. Settings is where nobody is standing
  // when the cap bites, and this is the one moment the answer is worth money to
  // the person reading it rather than only to us.
  const offer = plan === 'free' && !trialing && (spent || low);

  return (
    <div className={`alert ${spent ? 'error' : low ? 'warn' : 'info'}`}>
      <div className="row-between">
        <strong>
          {spent
            ? 'No photo scans left this month'
            : `${remaining} photo scan${remaining === 1 ? '' : 's'} left this month`}
        </strong>
        <span className="small muted">{used} of {limit}</span>
      </div>
      <div className="small muted" style={{ marginTop: 4 }}>
        {spent ? 'Resets' : 'Resets'} {resets}
        {plan === 'free' && (spent || low)
          ? ' — everything else in the app stays unlimited.'
          : '.'}
      </div>
      {trialing && (
        <div className="small muted" style={{ marginTop: 4 }}>
          Trial: {trialDays} day{trialDays === 1 ? '' : 's'} left.
        </div>
      )}
      {offer && <UpgradeOffer eligible={eligible} />}
    </div>
  );
}
